import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Popper,
  Paper,
  ClickAwayListener,
  Drawer,
  useMediaQuery,
  IconButton,
  Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { change } from "redux-form";
import dayjs, { Dayjs } from "dayjs";
import Input from "components/atoms/Input";
import CloseIcon from "assets/icons/close.svg";
import theme from "theme";
import { useScrollLock } from "hooks/useScrollLock";

// Shared state for date range picker (module-level)
interface SharedPickerState {
  open: boolean;
  activeAnchorRef: React.RefObject<HTMLDivElement> | null;
  activeFieldName: string | null;
  startDateAnchorRef: React.RefObject<HTMLDivElement> | null;
  endDateDiv: HTMLDivElement | null;
  isSelectingEnd: boolean;
}

let sharedPickerState: SharedPickerState = {
  open: false,
  activeAnchorRef: null,
  activeFieldName: null,
  startDateAnchorRef: null,
  endDateDiv: null,
  isSelectingEnd: false,
};

// Callbacks to notify all instances
const listeners = new Set<() => void>();

const notifyListeners = () => {
  listeners.forEach((listener) => listener());
};

interface DateRangePickerReduxStaticProps {
  input: {
    onChange: (value: any) => void;
    value: any;
    name: string;
  };
  label?: string;
  InputProps?: any;
  handleBlur?: (e: any) => void;
  handleFocus?: (e: any) => void;
  onOpenChange?: (open: boolean) => void;
  renderPickerOnly?: boolean;
  disableInternalOpen?: boolean;
  triggerOnlyProps?: any;
  onSelectionComplete?: () => void;
  meta: {
    error: string;
    touched: boolean;
    invalid: boolean;
  };
  DateRangePickerProps?: any;
  formName?: string;
  [key: string]: any;
}

// Helper: get days for a month
const getDaysInMonth = (month: number, year: number): Date[] => {
  const date = new Date(year, month, 1);
  const days: Date[] = [];
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
};

// Helper: get first day of week (0 = Sunday)
const getFirstDayOfWeek = (month: number, year: number): number => {
  return new Date(year, month, 1).getDay();
};

// Helper: check if date is in range
const isDateInRange = (
  date: Date,
  startDate: Dayjs | null,
  endDate: Dayjs | null
): boolean => {
  if (!startDate || !endDate) return false;
  const dateDayjs = dayjs(date);
  return (
    dateDayjs.isAfter(startDate, "day") && dateDayjs.isBefore(endDate, "day")
  );
};

// Helper: check if date is range start
const isRangeStart = (date: Date, startDate: Dayjs | null): boolean => {
  if (!startDate) return false;
  return dayjs(date).isSame(startDate, "day");
};

// Helper: check if date is range end
const isRangeEnd = (date: Date, endDate: Dayjs | null): boolean => {
  if (!endDate) return false;
  return dayjs(date).isSame(endDate, "day");
};

export default function DateRangePickerReduxStatic({
  input,
  label,
  InputProps,
  handleBlur,
  handleFocus,
  onOpenChange,
  renderPickerOnly = false,
  disableInternalOpen = false,
  triggerOnlyProps,
  onSelectionComplete,
  meta: { error, touched, invalid },
  DateRangePickerProps,
  formName = "DeliveryForm",
  ...rest
}: DateRangePickerReduxStaticProps) {
  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();
  const anchorRef = useRef<HTMLDivElement>(null);
  const containerDivRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [localOpen, setLocalOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Dayjs | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Dayjs | null>(null);
  const [hoverDate, setHoverDate] = useState<Dayjs | null>(null);
  const isSelectingRef = useRef(false); // Track if user is actively selecting
  
  // Only render picker if this is the active instance
  const isActive = sharedPickerState.activeAnchorRef === anchorRef;
  const shouldShowPicker = localOpen && isActive;

  // Get current form values from Redux
  const formValues = useAppSelector(
    (state) => state.form?.[formName]?.values || {}
  );

  const isEndDateField = input.name === "endDate";

  // Build date range from form values
  const startDate = useMemo(
    () =>
      formValues.pickupDate?.date
    ? dayjs(formValues.pickupDate.date)
        : null,
    [formValues.pickupDate?.date]
  );

  const endDate = useMemo(
    () =>
      formValues.endDate?.date ? dayjs(formValues.endDate.date) : null,
    [formValues.endDate?.date]
  );

  const currentValue = useMemo(
    () =>
      input.name === "pickupDate" ? formValues.pickupDate : formValues.endDate,
    [input.name, formValues.pickupDate, formValues.endDate]
  );

  // Calculate initial months to display
  const initialMonth = useMemo(() => {
    const baseDate = startDate || endDate || dayjs();
    return baseDate.month();
  }, [startDate, endDate]);

  const initialYear = useMemo(() => {
    const baseDate = startDate || endDate || dayjs();
    return baseDate.year();
  }, [startDate, endDate]);

  const [leftMonth, setLeftMonth] = useState(initialMonth);
  const [leftYear, setLeftYear] = useState(initialYear);
  const [rightMonth, setRightMonth] = useState(
    initialMonth === 11 ? 0 : initialMonth + 1
  );
  const [rightYear, setRightYear] = useState(
    initialMonth === 11 ? initialYear + 1 : initialYear
  );

  useScrollLock(!isMobile && shouldShowPicker);

  // Register end date div in shared state
  useEffect(() => {
    if (isEndDateField && containerDivRef.current) {
      sharedPickerState.endDateDiv = containerDivRef.current;
    }
  }, [isEndDateField]);

  // Update border based on selection state
  useEffect(() => {
    const updateBorder = () => {
      if (sharedPickerState.endDateDiv) {
        const color = sharedPickerState.isSelectingEnd ? "#520A76" : "#ECEBF2";
        sharedPickerState.endDateDiv.style.border = `1px solid ${color}`;
      }
    };
    
    listeners.add(updateBorder);
    updateBorder();
    
    return () => {
      listeners.delete(updateBorder);
    };
  }, []);

  // Sync open state with shared state
  useEffect(() => {
    const updateState = () => {
      setLocalOpen(sharedPickerState.open);
    };
    listeners.add(updateState);
    setLocalOpen(sharedPickerState.open);
    return () => {
      listeners.delete(updateState);
    };
  }, []);

  // Store start date anchor reference
  useEffect(() => {
    if (input.name === "pickupDate" && anchorRef.current) {
      sharedPickerState.startDateAnchorRef = anchorRef;
    }
  }, [input.name]);

  // Reset temp dates when picker opens (but not when user is actively selecting)
  useEffect(() => {
    if (shouldShowPicker && !isSelectingRef.current) {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
      setHoverDate(null);
      // Sync displayed months to selected range when opening
      if (startDate && endDate) {
        setLeftMonth(startDate.month());
        setLeftYear(startDate.year());
        if (startDate.isSame(endDate, "month")) {
          const nextMonth = startDate.add(1, "month");
          setRightMonth(nextMonth.month());
          setRightYear(nextMonth.year());
        } else {
          setRightMonth(endDate.month());
          setRightYear(endDate.year());
        }
      } else if (startDate) {
        setLeftMonth(startDate.month());
        setLeftYear(startDate.year());
        const nextMonth = startDate.add(1, "month");
        setRightMonth(nextMonth.month());
        setRightYear(nextMonth.year());
      } else if (endDate) {
        setRightMonth(endDate.month());
        setRightYear(endDate.year());
        const prevMonth = endDate.subtract(1, "month");
        setLeftMonth(prevMonth.month());
        setLeftYear(prevMonth.year());
      }
    }
  }, [shouldShowPicker, startDate, endDate]);

  const handleOpen = useCallback(() => {
    // Force close any existing open pickers first
    if (
      sharedPickerState.open &&
      sharedPickerState.activeAnchorRef !== anchorRef
    ) {
      sharedPickerState.open = false;
      sharedPickerState.activeAnchorRef = null;
      sharedPickerState.activeFieldName = null;
      notifyListeners();
    }

    // Small delay to ensure cleanup is complete
    setTimeout(() => {
      sharedPickerState.open = true;
      sharedPickerState.activeAnchorRef = anchorRef;
      sharedPickerState.activeFieldName = input.name;
      setLocalOpen(true);
      onOpenChange?.(true);
      notifyListeners();
    }, 0);
  }, [input.name, onOpenChange]);

  const handleClose = useCallback(() => {
    if (sharedPickerState.activeAnchorRef === anchorRef) {
      (document.activeElement as HTMLElement | null)?.blur?.();
      (anchorRef.current?.querySelector("input") as HTMLElement | null)?.blur?.();
      sharedPickerState.open = false;
      sharedPickerState.activeAnchorRef = null;
      sharedPickerState.isSelectingEnd = false;
      setLocalOpen(false);
      setTempStartDate(null);
      setTempEndDate(null);
      setHoverDate(null);
      isSelectingRef.current = false;
      onOpenChange?.(false);
      notifyListeners();
    }
  }, [onOpenChange]);
   
  // Update Redux immediately when dates change
  const updateReduxDates = useCallback(
    (newStart: Dayjs | null, newEnd: Dayjs | null) => {
    if (isEndDateField) {
      // When end date field is active, only update end date
        if (newEnd) {
        dispatch(
          change(formName, "endDate", {
              date: newEnd.toISOString(),
            error: false,
          })
        );
      } else {
        dispatch(change(formName, "endDate", null));
      }
    } else {
      // When start date field is active, allow full range selection
        if (newStart) {
        dispatch(
          change(formName, "pickupDate", {
              date: newStart.toISOString(),
            error: false,
          })
        );
      } else {
        dispatch(change(formName, "pickupDate", null));
      }

        if (newEnd) {
        dispatch(
          change(formName, "endDate", {
              date: newEnd.toISOString(),
            error: false,
          })
        );
        } else if (!newStart) {
          dispatch(change(formName, "endDate", null));
        }
      }
    },
    [isEndDateField, formName, dispatch]
  );

  const handleDateClick = useCallback(
    (date: Date) => {
      const dateDayjs = dayjs(date);
      const today = dayjs().startOf("day");

      // Disable past dates
      if (dateDayjs.isBefore(today, "day")) {
        return;
      }

      // If selecting end date field, only allow end date selection
      if (isEndDateField) {
        const newEnd = dateDayjs;
        setTempEndDate(newEnd);
        sharedPickerState.isSelectingEnd = false;
        notifyListeners();
        // Update Redux immediately and close
        updateReduxDates(startDate, newEnd);
        handleClose();
        onSelectionComplete?.();
        return;
      }

      // For start date field, handle range selection
      let newStart: Dayjs | null = tempStartDate;
      let newEnd: Dayjs | null = tempEndDate;
      let shouldClose = false;

      // Mark that user is actively selecting
      isSelectingRef.current = true;

      if (!tempStartDate || (tempStartDate && tempEndDate)) {
        // Start new selection - user is changing start date
        // Only update temp state, don't update Redux yet
        newStart = dateDayjs;
        newEnd = null; // Clear temp end date so user can select new one
        // After selecting a start date, highlight the end date field
        sharedPickerState.isSelectingEnd = true;
        notifyListeners();
        
        // Ensure picker stays open
        if (sharedPickerState.activeAnchorRef === anchorRef) {
          sharedPickerState.open = true;
          setLocalOpen(true);
        }
        
        // Update Redux immediately for start date only (so input shows the change)
        // Keep existing endDate in Redux - don't clear it
        if (newStart) {
          dispatch(
            change(formName, "pickupDate", {
              date: newStart.toISOString(),
              error: false,
            })
          );
        }
        // Don't update endDate in Redux - keep existing one
        // Reset selecting flag after a short delay to allow useEffect to skip
        setTimeout(() => {
          isSelectingRef.current = false;
        }, 100);
      } else if (tempStartDate && !tempEndDate) {
        // Complete the range - user is selecting end date
        if (dateDayjs.isBefore(tempStartDate, "day")) {
          // If clicked date is before start, make it the new start
          newStart = dateDayjs;
          newEnd = tempStartDate;
        } else {
          // Normal case: set end date
          newEnd = dateDayjs;
        }
        sharedPickerState.isSelectingEnd = false;
        notifyListeners();
        // Update Redux with complete range and close
        updateReduxDates(newStart, newEnd);
        shouldClose = true;
        isSelectingRef.current = false;
      }

      setTempStartDate(newStart);
      setTempEndDate(newEnd);

      // Auto-close only when completing range selection (end date selected)
      if (shouldClose && newStart && newEnd) {
        handleClose();
        onSelectionComplete?.();
      }
    },
    [isEndDateField, startDate, tempStartDate, tempEndDate, updateReduxDates, handleClose, dispatch, formName]
  );

  const handleDateHover = useCallback(
    (date: Date | null) => {
      if (!date) {
        setHoverDate(null);
        return;
      }

      const dateDayjs = dayjs(date);
      const today = dayjs().startOf("day");

      // Don't show hover for past dates
      if (dateDayjs.isBefore(today, "day")) {
        setHoverDate(null);
        return;
      }

      // Don't show hover if selecting end date field and date is before start
      if (isEndDateField && startDate && dateDayjs.isBefore(startDate, "day")) {
        setHoverDate(null);
        return;
      }

      setHoverDate(dateDayjs);
    },
    [isEndDateField, startDate]
  );

  const dateValue = useMemo(
    () =>
      currentValue?.date
    ? dayjs(currentValue.date).format("DD/MM/YYYY")
        : "",
    [currentValue?.date]
  );

  const fieldId = useMemo(
    () =>
    InputProps?.id ||
      `${input.name}-${Math.random().toString(36).substr(2, 9)}`,
    [InputProps?.id, input.name]
  );

  const inputProps = useMemo(
    () => ({
      autoComplete: "off" as const,
      id: fieldId,
      InputLabelProps: { shrink: true },
      ...InputProps,
      onKeyDown: (e: any) => {
        e.preventDefault();
      },
      error: touched && invalid && error ? true : false,
      value: dateValue,
      placeholder:
        label ||
        (input.name === "pickupDate" ? t("startDate") : t("endDate")) ||
        "Select Date",
      readOnly: true,
      inputProps: {
        ...InputProps?.inputProps,
        readOnly: true,
        inputMode: "none" as const,
      },
      helperText: touched && invalid && error ? error : "",
      className: shouldShowPicker && isActive ? "date-field-active" : "",
    }),
    [
      fieldId,
      InputProps,
      handleOpen,
      touched,
      invalid,
      error,
      dateValue,
      label,
      input.name,
      t,
      shouldShowPicker,
      isActive,
    ]
  );

  // Render a single month calendar
  const renderMonthCalendar = (
    month: number,
    year: number,
    isLeft: boolean,
    showPrev: boolean,
    showNext: boolean
  ) => {
    const days = getDaysInMonth(month, year);
    const firstDayOfWeek = getFirstDayOfWeek(month, year);
    const paddedDays = [...Array(firstDayOfWeek).fill(null), ...days];
    const today = dayjs().startOf("day");

    // Use temp dates if available, otherwise use actual dates
    const displayStart = tempStartDate || startDate;
    const displayEnd = tempEndDate || endDate;

    // Calculate hover range for preview
    let hoverStart: Dayjs | null = null;
    let hoverEnd: Dayjs | null = null;

    if (hoverDate && !isEndDateField) {
      // When hovering and we have a start date but no end date, show preview range
      if (displayStart && !displayEnd) {
        if (hoverDate.isAfter(displayStart, "day")) {
          hoverStart = displayStart;
          hoverEnd = hoverDate;
        } else if (hoverDate.isBefore(displayStart, "day")) {
          hoverStart = hoverDate;
          hoverEnd = displayStart;
        }
      }
    }

    return (
      <Box sx={{ width: "100%" }}>
        {/* Month Header with navigation */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <IconButton
            size="small"
            disabled={
              !showPrev ||
              leftYear < dayjs().year() ||
              (leftYear === dayjs().year() && leftMonth <= dayjs().month())
            }
            onClick={() => {
              if (!showPrev) return;
              if (leftMonth === 0) {
                setLeftYear((y) => y - 1);
                setLeftMonth(11);
                setRightMonth(0);
                setRightYear((y) => y - 1);
              } else {
                setLeftMonth((m) => m - 1);
                setRightMonth((m) => m - 1);
              }
            }}
            sx={{
              color: "black",
              border: "1px solid #ECEBF2",
              borderRadius: "8px",
              padding: "7px",
              visibility: showPrev ? "visible" : "hidden",
              "&:hover": {
                backgroundColor: "#F9F9F9",
              },
            }}
          >
            {i18n.language === "en" ? (
              <ArrowBackIosNewIcon fontSize="small" />
            ) : (
              <ArrowForwardIosIcon fontSize="small" />
            )}
          </IconButton>

          <Typography fontWeight={700} fontSize={14} textAlign="center">
            {new Date(year, month).toLocaleString(i18n.language, {
              month: "long",
              year: "numeric",
            })}
          </Typography>

          <IconButton
            size="small"
            onClick={() => {
              if (!showNext) return;
              if (rightMonth === 11) {
                setRightYear((y) => y + 1);
                setRightMonth(0);
                setLeftMonth(11);
                setLeftYear((y) => y + 1);
              } else {
                setRightMonth((m) => m + 1);
                setLeftMonth((m) => m + 1);
              }
            }}
            sx={{
              color: "black",
              border: "1px solid #ECEBF2",
              borderRadius: "8px",
              padding: "7px",
              visibility: showNext ? "visible" : "hidden",
              "&:hover": {
                backgroundColor: "#F9F9F9",
              },
            }}
          >
            {i18n.language === "en" ? (
              <ArrowForwardIosIcon fontSize="small" />
            ) : (
              <ArrowBackIosNewIcon fontSize="small" />
            )}
          </IconButton>
        </Box>

        {/* Days of week */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: "8px",
            mb: 1,
          }}
        >
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
            <Box
              key={d}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 0,
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                align="center"
                sx={{ color: "#666" }}
              >
                {d}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: "8px",
            mt: 1,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Selected range background - continuous background without gaps */}
          {displayStart && displayEnd && (() => {
            const rangeDates: { day: Date; index: number; row: number; col: number }[] = [];

            paddedDays.forEach((day, index) => {
              if (!day) return;
              const isInRange = isDateInRange(day, displayStart, displayEnd);
              const isRangeStartDate = isRangeStart(day, displayStart);
              const isRangeEndDate = isRangeEnd(day, displayEnd);

              if (isInRange || isRangeStartDate || isRangeEndDate) {
                const row = Math.floor(index / 7);
                const col = index % 7;
                rangeDates.push({ day, index, row, col });
              }
            });

            if (rangeDates.length === 0) return null;

            const rows = rangeDates.reduce((acc, item) => {
              if (!acc[item.row]) acc[item.row] = [];
              acc[item.row].push(item);
              return acc;
            }, {} as Record<number, typeof rangeDates>);

            const rowKeys = Object.keys(rows).map(Number);
            const minRow = Math.min(...rowKeys);
            const maxRow = Math.max(...rowKeys);
            const colPercent = 100 / 7;

            return (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  pointerEvents: "none",
                  zIndex: 0,
                }}
              >
                {Object.entries(rows).map(([rowStr, dates]) => {
                  const row = parseInt(rowStr);
                  const sortedDates = dates.sort((a, b) => a.col - b.col);
                  const firstDate = sortedDates[0];
                  const lastDate = sortedDates[sortedDates.length - 1];

                  const isFirstRow = row === minRow;
                  const isLastRow = row === maxRow;
                  const isSingleRow = isFirstRow && isLastRow;

                  const left = isFirstRow ? `${firstDate.col * colPercent}%` : "0%";
                  const width = isSingleRow
                    ? `${(lastDate.col - firstDate.col + 1) * colPercent}%`
                    : isLastRow
                    ? `${(lastDate.col + 1) * colPercent}%`
                    : isFirstRow
                    ? `${(7 - firstDate.col) * colPercent}%`
                    : "100%";

                  const rowHeight = "35px";

                  return (
                    <Box
                      key={`selected-row-${row}`}
                      sx={{
                        position: "absolute",
                        left: left,
                      top: `calc(${row} * (35px + 8px))`,
                        width: width,
                        height: rowHeight,
                        backgroundColor: "#520A761A",
                        borderRadius: "50px",
                      }}
                    />
                  );
                })}
              </Box>
            );
          })()}

          {/* Hover range background - continuous background without gaps */}
          {hoverStart && hoverEnd && !isEndDateField && (() => {
            // Find all dates in the hover range within this month
            const hoverDates: { day: Date; index: number; row: number; col: number }[] = [];

            paddedDays.forEach((day, index) => {
              if (!day) return;
              const isInHoverRange = isDateInRange(day, hoverStart, hoverEnd);
              const isHoverStart = isRangeStart(day, hoverStart);
              const isHoverEnd = isRangeEnd(day, hoverEnd);

              if (isInHoverRange || isHoverStart || isHoverEnd) {
                const row = Math.floor(index / 7);
                const col = index % 7;
                hoverDates.push({ day, index, row, col });
              }
            });

            if (hoverDates.length === 0) return null;

            // Group by row
            const rows = hoverDates.reduce((acc, item) => {
              if (!acc[item.row]) acc[item.row] = [];
              acc[item.row].push(item);
              return acc;
            }, {} as Record<number, typeof hoverDates>);

            const rowKeys = Object.keys(rows).map(Number);
            const minRow = Math.min(...rowKeys);
            const maxRow = Math.max(...rowKeys);
            const colPercent = 100 / 7;

            return (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  pointerEvents: "none",
                  zIndex: 0,
                }}
              >
                {Object.entries(rows).map(([rowStr, dates]) => {
                  const row = parseInt(rowStr);
                  const sortedDates = dates.sort((a, b) => a.col - b.col);
                  const firstDate = sortedDates[0];
                  const lastDate = sortedDates[sortedDates.length - 1];

                  const isFirstRow = row === minRow;
                  const isLastRow = row === maxRow;
                  const isSingleRow = isFirstRow && isLastRow;

                  const left = isFirstRow ? `${firstDate.col * colPercent}%` : "0%";
                  const width = isSingleRow
                    ? `${(lastDate.col - firstDate.col + 1) * colPercent}%`
                    : isLastRow
                    ? `${(lastDate.col + 1) * colPercent}%`
                    : isFirstRow
                    ? `${(7 - firstDate.col) * colPercent}%`
                    : "100%";

                  const rowHeight = "35px";

                  return (
                    <Box
                      key={`hover-row-${row}`}
                      sx={{
                        position: "absolute",
                        left: left,
                      top: `calc(${row} * (35px + 8px))`,
                        width: width,
                        height: rowHeight,
                        backgroundColor: "#520A761A",
                        borderRadius: "50px",
                      }}
                    />
                  );
                })}
              </Box>
            );
          })()}
          
          {paddedDays.map((day, index) => {
            if (!day) {
              return <Box key={`empty-${index}`} sx={{ position: "relative", zIndex: 1 }} />;
            }

            const dateDayjs = dayjs(day);
            const isPast = dateDayjs.isBefore(today, "day");
            const isStart = isRangeStart(day, displayStart);
            const isEnd = isRangeEnd(day, displayEnd);
            
            // Check if date is in hover preview range
            const isHoverStart = hoverStart && isRangeStart(day, hoverStart);
            const isHoverEnd = hoverEnd && isRangeEnd(day, hoverEnd);
            
            const isSelected = isStart || isEnd;
            const isHoverSelected = isHoverStart || isHoverEnd;

            // Disable logic
            const isDisabled = isPast;

            // Do not disable the left calendar; only disable past dates via isDisabled
            const shouldDisable = false;

            return (
              <Box
                key={day.toISOString()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 0,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <IconButton
                  disabled={isDisabled || shouldDisable}
                  onClick={() => !shouldDisable && handleDateClick(day)}
                  onMouseEnter={() => !isMobile && !shouldDisable && !isDisabled && handleDateHover(day)}
                  onMouseLeave={() => handleDateHover(null)}
                  sx={{
                    width: "100%",
                    maxWidth: 35,
                    height: 35,
                    borderRadius: "50% !important",
                    border: "none",
                    backgroundColor: isSelected
                      ? "#520A76"
                      : isHoverSelected
                      ? "#520A76"
                      : "transparent",
                    "&:hover": {
                      backgroundColor:
                        isSelected || isHoverSelected
                          ? "#520A76"
                          : isDisabled || shouldDisable
                          ? "transparent"
                          : "#F9F9F9",
                    },
                    "&:disabled": {
                      backgroundColor: "transparent",
                      cursor: "not-allowed",
                      opacity: shouldDisable ? 0.5 : 0.3,
                    },
                    "& .MuiTypography-root": {
                      color: isSelected || isHoverSelected
                        ? "#fff"
                        : isDisabled || shouldDisable
                        ? "#00000066"
                        : "#0A0A0E",
                      fontWeight: 700,
                      fontSize: "10.5px",
                    },
                    "&:disabled .MuiTypography-root": {
                      color: "#00000066 !important",
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      fontSize: "10.5px",
                    }}
                  >
                    {day.getDate()}
                  </Typography>
                </IconButton>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const CalendarContent = () => (
    <Box
      sx={{
        width: isMobile ? "100%" : "fit-content",
        minWidth: isMobile ? "100%" : "600px",
        display: "flex",
        justifyContent: "center",
        p: isMobile ? 0 : 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: "100%",
        }}
      >
        {/* Two calendars side by side */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: 3,
          }}
        >
          {isMobile ? (
            renderMonthCalendar(leftMonth, leftYear, true, true, true)
          ) : (
            <>
              {renderMonthCalendar(leftMonth, leftYear, true, true, false)}
              {renderMonthCalendar(rightMonth, rightYear, false, false, true)}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );

  const DrawerComponent = ({
    open: drawerOpen,
    onClose,
    children,
  }: {
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
  }) => {
    return (
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={onClose}
        ModalProps={
          isMobile
            ? {
                disableAutoFocus: true,
                disableEnforceFocus: true,
                disableRestoreFocus: true,
              }
            : undefined
        }
        PaperProps={{
          style: {
            borderTopLeftRadius: 18,
            borderTopRightRadius: 18,
            padding: "16px",
            minHeight: "50vh",
          },
        }}
        sx={{ zIndex: 1400 }}
      >
        <Box
          component={"img"}
          src={CloseIcon}
          alt="X"
          top={"16px"}
          position={"absolute"}
          onClick={() => onClose()}
          right={i18n.language === "ar" ? "auto" : "16px"}
          left={i18n.language === "ar" ? "16px" : "auto"}
          sx={{ cursor: "pointer" }}
        />
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <h3 style={{ margin: 0, fontSize: "16px" }}>
            {label || t("date") || "Select Date Range"}
          </h3>
        </Box>
        {children}
      </Drawer>
    );
  };

  const baseClass =
    i18n.language === "en" ? "date__field" : "date__field arDate__field";

  if (renderPickerOnly) {
    return <CalendarContent />;
  }

  return (
    <>
      <div
        ref={containerDivRef}
        className={baseClass}
        style={{ border: "1px solid #ECEBF2" }}
      >
        <div
          ref={anchorRef}
          onMouseDown={triggerOnlyProps?.onMouseDown}
          onClick={triggerOnlyProps?.onClick || (disableInternalOpen ? undefined : handleOpen)}
          onKeyDown={(e) => {
            if (triggerOnlyProps?.onKeyDown) {
              triggerOnlyProps.onKeyDown(e);
              return;
            }
            if (!disableInternalOpen && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              handleOpen();
            }
          }}
          role="button"
          tabIndex={0}
          style={{ cursor: "pointer" }}
        >
          <Input
            {...inputProps}
            customLabelStyles={true}
            label={label}
            onFocusCapture={triggerOnlyProps?.onFocusCapture}
            inputProps={{
              ...inputProps.inputProps,
              ...triggerOnlyProps?.inputProps,
            }}
            sx={{
              pointerEvents: "none",
              "&:hover": { cursor: "pointer" },
            }}
          />
        </div>
      </div>

      {shouldShowPicker &&
        (isMobile ? (
          <DrawerComponent open={shouldShowPicker} onClose={handleClose}>
            <CalendarContent />
          </DrawerComponent>
        ) : (
          <Popper
            open={shouldShowPicker}
            anchorEl={
              sharedPickerState.startDateAnchorRef?.current || anchorRef.current
            }
            placement="bottom-start"
            sx={{ zIndex: 1500, mt: 1 }}
          >
            <ClickAwayListener
              onClickAway={(event) => {
                const target = event.target as HTMLElement;
                if (anchorRef.current && anchorRef.current.contains(target)) {
                  return;
                }
                handleClose();
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  borderRadius: '22px',
                  mt: 2,
                  boxShadow: "0px 4px 16px rgba(0,0,0,0.3)",
                }}
              >
                <CalendarContent />
              </Paper>
            </ClickAwayListener>
          </Popper>
        ))}
    </>
  );
}
